/**
 * The TSE search-and-link picker (Figma node `96:2`, `Organização · Nova campanha (TSE)`,
 * file `LrdQopN1jNWKJFWxOkn0IY`): the `Origem` segmented toggle through `Resultado`. Mounts
 * inside `NewCampaignDialog`, above that dialog's own name field -- the frame draws the name
 * field (`Nome da campanha`) below this picker, not above it.
 *
 * The frame's `Território` block (municipality/neighbourhood counts for the selected state) is
 * `LocalityStrip`, a sibling mounted in `NewCampaignDialog` rather than here: it reads the
 * picked candidature's own `tse_election_id`/`tse_registration_id` off this component's `onChange`
 * output, not a prop this component takes.
 *
 * Both modes lay out Cargo, Estado, Número. In TSE mode Cargo offers the chosen election's own
 * offices and Estado the units its roster holds for that office (`useTseUnits`), hidden for a
 * national office; by hand, Cargo is TSE's office table (`useTseOffices`) and Estado the tenant's
 * state places (`useStates`). Every candidature is offered, its status shown beside it.
 *
 * `onSuggestName` and `onElectionDateChange` let the caller propose a campaign name and dates
 * without this component knowing anything about those fields' own touched/lock state -- that
 * judgment call belongs to whoever owns them.
 *
 * A registration's `full_name` exists only while this component holds the search result:
 * `Candidate` (the shared record schema this emits) has no field for it. Its office, number and
 * UF are `Candidate` fields, which `api` re-reads from the roster row on save.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { Link as LinkIcon } from 'lucide-react';

import { IconHint } from '../../../chrome/hint';
import { Avatar, AvatarFallback } from '../../../ui/avatar';
import { Badge } from '../../../ui/badge';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '../../../ui/command';
import { Field, FieldError, FieldLabel } from '../../../ui/field';
import { Input } from '../../../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../../../ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { Skeleton } from '../../../ui/skeleton';
import { SEGMENTED_ITEM_CLASSNAME, ToggleGroup, ToggleGroupItem } from '../../../ui/toggle-group';
import type { ApiFailureError } from '../../../lib/http/errors';
import { useCan } from '../../session/orgContext';
import { rm } from '../record/recordMessages';
import {
  useElections,
  useStates,
  useTseCandidates,
  useTseOffices,
  useTseUnits,
} from './api/queries';
import {
  candidateFromRegistration,
  officeLabel,
  sentenceCase,
  tseRegistrationUrl,
  type LinkedCandidate,
  type Registration,
} from './api/schemas';
import { tse } from './tseMessages';

const WRITE_CAPABILITY = 'control_plane.write';
const DEBOUNCE_MS = 250;

type Mode = 'tse' | 'manual';

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

function initialsOf(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((part) => part !== '');
  const first = parts[0]?.charAt(0) ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : '';
  return (first + last).toUpperCase();
}

function nullIfEmpty(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

interface ManualFields {
  name: string;
  office: string;
  ballotNumber: string;
  uf: string;
  party: string;
  coalition: string;
}

const EMPTY_MANUAL_FIELDS: ManualFields = {
  name: '',
  office: '',
  ballotNumber: '',
  uf: '',
  party: '',
  coalition: '',
};

function manualPayload(fields: ManualFields): LinkedCandidate {
  const name = fields.name.trim();
  if (name === '') return null;
  return {
    name,
    office: nullIfEmpty(fields.office),
    ballot_number: nullIfEmpty(fields.ballotNumber),
    uf: nullIfEmpty(fields.uf),
    party: nullIfEmpty(fields.party),
    coalition: nullIfEmpty(fields.coalition),
    tse_election_id: null,
    tse_registration_id: null,
  };
}

interface ReferenceMessages {
  placeholder: string;
  loading: string;
  empty: string;
  error: string;
}

/** A Select over a fetched closed list, whose placeholder says the list is loading or empty and
 *  whose failure is a field error, so no state reads as a list with nothing to pick for no
 *  reason. */
function ReferenceSelect<T>(props: {
  id: string;
  value: string;
  onValueChange: (value: string) => void;
  query: UseQueryResult<T[], ApiFailureError>;
  option: (item: T) => { value: string; label: string };
  messages: ReferenceMessages;
  disabled?: boolean;
}): ReactNode {
  const errorId = useId();
  const { query, messages } = props;
  const items = query.data ?? [];
  // A query held back until its inputs exist is not loading: it waits on the reader.
  const loading = query.isPending && query.fetchStatus !== 'idle';
  const placeholder = loading
    ? messages.loading
    : query.isError
      ? messages.error
      : items.length === 0
        ? messages.empty
        : messages.placeholder;
  return (
    <>
      <Select
        value={props.value}
        onValueChange={props.onValueChange}
        disabled={props.disabled === true || !query.isSuccess || items.length === 0}
      >
        <SelectTrigger
          id={props.id}
          className="w-full"
          aria-invalid={query.isError || undefined}
          aria-describedby={query.isError ? errorId : undefined}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {items.map((item) => {
            const { value, label } = props.option(item);
            return (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
      {query.isError && <FieldError id={errorId}>{messages.error}</FieldError>}
    </>
  );
}

function officeMessages(): ReferenceMessages {
  return {
    placeholder: tse('office.placeholder'),
    loading: tse('office.loading'),
    empty: tse('office.empty'),
    error: tse('office.error'),
  };
}

function stateMessages(): ReferenceMessages {
  return {
    placeholder: tse('state.placeholder'),
    loading: tse('state.loading'),
    empty: tse('state.empty'),
    error: tse('state.error'),
  };
}

export interface TseCandidatePickerProps {
  tenantId: string;
  value: LinkedCandidate;
  onChange: (candidate: LinkedCandidate) => void;
  /** Fired with `${ballot_name} ${ballot_number}` when a TSE registration is picked. */
  onSuggestName?: (name: string) => void;
  /** The selected election's own `election_date`, `null` while none is selected. `Election`
   *  carries a single date, not a start/end period, so this is the one value a caller has to
   *  autofill a period from. */
  onElectionDateChange?: (date: string | null) => void;
}

export function TseCandidatePicker(props: TseCandidatePickerProps): ReactNode {
  const canLink = useCan(WRITE_CAPABILITY);
  const [mode, setMode] = useState<Mode>('tse');
  const [electionId, setElectionId] = useState('');
  const [office, setOffice] = useState<string | null>(null);
  const [electoralUnit, setElectoralUnit] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Registration | null>(null);
  const [manualFields, setManualFields] = useState<ManualFields>(EMPTY_MANUAL_FIELDS);
  const debouncedQuery = useDebounced(query, DEBOUNCE_MS);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const elections = useElections(props.tenantId);
  const offices = useTseOffices(props.tenantId);
  const states = useStates(props.tenantId);
  const election = elections.data?.find((candidate) => candidate.id === electionId) ?? null;

  // One election in almost every cycle: chosen for the reader rather than left on an empty
  // Select they have no reason to open.
  useEffect(() => {
    if (electionId === '' && elections.data !== undefined && elections.data.length > 0) {
      setElectionId(elections.data[0]!.id);
    }
  }, [electionId, elections.data]);

  useEffect(() => {
    props.onElectionDateChange?.(election?.election_date ?? null);
  }, [election?.election_date]);

  const units = useTseUnits(props.tenantId, electionId, office);
  const stateNames = new Map((states.data ?? []).map((place) => [place.uf ?? '', place.name]));
  // A national office's one unit is `BR`: there is no state to choose.
  const national = units.data !== undefined && units.data.every((unit) => unit === 'BR');

  const candidates = useTseCandidates(props.tenantId, {
    electionId,
    office,
    electoralUnit,
    ballotNumber: null,
    q: debouncedQuery,
  });
  const searchEnabled = open && electionId !== '';

  const nameId = useId();
  const partyId = useId();
  const coalitionId = useId();
  const electionSelectId = useId();
  const officeSelectId = useId();
  const stateSelectId = useId();
  const numberId = useId();
  const manualOfficeId = useId();
  const manualNumberId = useId();
  const manualNumberErrorId = useId();
  const manualStateId = useId();

  if (!canLink) return null;

  const manualDigits =
    offices.data?.find((item) => item.name === manualFields.office)?.ballot_number_digits ?? null;
  const manualNumberError =
    manualFields.ballotNumber === ''
      ? null
      : manualDigits === null
        ? tse('number.needsOffice')
        : manualFields.ballotNumber.length !== manualDigits
          ? tse('number.digits', {
              office: officeLabel(manualFields.office),
              digits: String(manualDigits),
            })
          : null;

  function selectRegistration(registration: Registration): void {
    setSelected(registration);
    props.onChange(candidateFromRegistration(registration));
    props.onSuggestName?.(`${registration.ballot_name} ${registration.ballot_number}`);
    setOpen(false);
    // Reopening the search starts blank -- a stale query left over from the last pick would
    // otherwise sit ahead of whatever the reader types next, searching for both at once.
    setQuery('');
    triggerRef.current?.focus();
  }

  function changeMode(next: string): void {
    if (next !== 'tse' && next !== 'manual') return;
    setMode(next);
    setSelected(null);
    setManualFields(EMPTY_MANUAL_FIELDS);
    props.onChange(null);
  }

  function changeManualField<K extends keyof ManualFields>(key: K, fieldValue: string): void {
    const next = { ...manualFields, [key]: fieldValue };
    setManualFields(next);
    props.onChange(manualPayload(next));
  }

  return (
    <div className="flex flex-col gap-4">
      <ToggleGroup
        type="single"
        spacing={1}
        value={mode}
        onValueChange={changeMode}
        aria-label={tse('mode.tse')}
        className="w-fit bg-secondary rounded-pill p-1"
      >
        <ToggleGroupItem value="tse" className={SEGMENTED_ITEM_CLASSNAME}>
          {tse('mode.tse')}
        </ToggleGroupItem>
        <ToggleGroupItem value="manual" className={SEGMENTED_ITEM_CLASSNAME}>
          {tse('mode.manual')}
        </ToggleGroupItem>
      </ToggleGroup>

      {mode === 'manual' && (
        <div className="flex flex-col gap-4">
          <Field>
            <FieldLabel htmlFor={nameId}>{rm('candidate.name.label')}</FieldLabel>
            <Input
              id={nameId}
              value={manualFields.name}
              onChange={(event) => changeManualField('name', event.target.value)}
            />
          </Field>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Field className="min-w-0">
              <FieldLabel htmlFor={manualOfficeId}>{rm('candidate.office.label')}</FieldLabel>
              <ReferenceSelect
                id={manualOfficeId}
                value={manualFields.office}
                onValueChange={(next) => changeManualField('office', next)}
                query={offices}
                option={(item) => ({ value: item.name, label: officeLabel(item.name) })}
                messages={officeMessages()}
              />
            </Field>
            <Field className="min-w-0">
              <FieldLabel htmlFor={manualStateId}>{tse('state.label')}</FieldLabel>
              <ReferenceSelect
                id={manualStateId}
                value={manualFields.uf}
                onValueChange={(next) => changeManualField('uf', next)}
                query={states}
                option={(place) => ({ value: place.uf ?? '', label: place.name })}
                messages={stateMessages()}
              />
            </Field>
            <Field className="min-w-0" data-invalid={manualNumberError !== null || undefined}>
              <FieldLabel htmlFor={manualNumberId}>{tse('number.label')}</FieldLabel>
              <Input
                id={manualNumberId}
                inputMode="numeric"
                autoComplete="off"
                maxLength={manualDigits ?? 5}
                value={manualFields.ballotNumber}
                onChange={(event) =>
                  changeManualField('ballotNumber', event.target.value.replace(/\D/g, ''))
                }
                aria-invalid={manualNumberError !== null || undefined}
                aria-describedby={manualNumberError !== null ? manualNumberErrorId : undefined}
              />
              <FieldError id={manualNumberErrorId}>{manualNumberError}</FieldError>
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor={partyId}>{rm('candidate.party.label')}</FieldLabel>
            <Input
              id={partyId}
              value={manualFields.party}
              onChange={(event) => changeManualField('party', event.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor={coalitionId}>{rm('candidate.coalition.label')}</FieldLabel>
            <Input
              id={coalitionId}
              value={manualFields.coalition}
              onChange={(event) => changeManualField('coalition', event.target.value)}
            />
          </Field>
        </div>
      )}

      {mode === 'tse' && (
        <div className="flex flex-col gap-4">
          <Field>
            <div className="flex items-center gap-1.5">
              <FieldLabel
                htmlFor={electionSelectId}
                className="flex-1 font-normal text-muted-foreground"
              >
                {tse('election.label')}
              </FieldLabel>
              {election !== null && (
                <Badge variant="secondary" className="rounded-full">
                  {tse(`scope.${election.scope}`)}
                </Badge>
              )}
            </div>
            <Select
              value={electionId}
              onValueChange={(next) => {
                setElectionId(next);
                setOffice(null);
                setElectoralUnit(null);
              }}
              disabled={elections.isPending || elections.data?.length === 0}
            >
              <SelectTrigger id={electionSelectId} className="w-full">
                <SelectValue placeholder={tse('election.placeholder')} />
              </SelectTrigger>
              <SelectContent>
                {(elections.data ?? []).map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {tse('election.cycle', { year: String(item.cycle_year) })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Field className="min-w-0">
              <FieldLabel htmlFor={officeSelectId} className="font-normal text-muted-foreground">
                {tse('office.label')}
              </FieldLabel>
              <Select
                value={office ?? ''}
                onValueChange={(next) => {
                  setOffice(next);
                  setElectoralUnit(null);
                }}
                disabled={election === null || election.offices.length === 0}
              >
                <SelectTrigger id={officeSelectId} className="w-full">
                  <SelectValue
                    placeholder={
                      election !== null && election.offices.length === 0
                        ? tse('office.empty')
                        : tse('office.placeholder')
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {(election?.offices ?? []).map((name) => (
                    <SelectItem key={name} value={name}>
                      {officeLabel(name)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            {!national && (
              <Field className="min-w-0">
                <FieldLabel htmlFor={stateSelectId} className="font-normal text-muted-foreground">
                  {tse('state.label')}
                </FieldLabel>
                <ReferenceSelect
                  id={stateSelectId}
                  value={electoralUnit ?? ''}
                  onValueChange={(next) => setElectoralUnit(next)}
                  query={units}
                  option={(unit) => ({ value: unit, label: stateNames.get(unit) ?? unit })}
                  messages={stateMessages()}
                  disabled={office === null}
                />
              </Field>
            )}

            <Field className="min-w-0">
              <FieldLabel htmlFor={numberId} className="font-normal text-muted-foreground">
                {tse('number.label')}
              </FieldLabel>
              <Popover
                open={open}
                onOpenChange={(next) => {
                  if (electionId === '') return;
                  setOpen(next);
                }}
              >
                <PopoverTrigger asChild>
                  <button
                    id={numberId}
                    ref={triggerRef}
                    type="button"
                    disabled={electionId === ''}
                    aria-haspopup="listbox"
                    className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-card px-3 py-2 text-body-sm disabled:opacity-50"
                  >
                    <span className="truncate">
                      {selected?.ballot_number ?? tse('number.placeholder')}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-80 p-0" aria-label={tse('number.label')}>
                  {/* `CommandInput`'s `aria-controls` names `CommandList`'s id unconditionally
                   *  (cmdk), so the list stays mounted even with nothing to show -- an unmounted
                   *  list leaves that id dangling (axe: aria-valid-attr-value). Loading/error/empty
                   *  states render as a disabled `CommandItem` rather than a bare div, because a
                   *  `listbox` with a non-option child also fails axe's aria-required-children. */}
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder={tse('number.placeholder')}
                      value={query}
                      onValueChange={setQuery}
                    />
                    <CommandList>
                      <CommandGroup>
                        {searchEnabled && candidates.isPending && (
                          <CommandItem
                            value="__loading__"
                            disabled
                            aria-label={tse('search.loading')}
                            className="flex flex-col gap-2"
                          >
                            <Skeleton className="h-9 w-full" />
                            <Skeleton className="h-9 w-full" />
                            <Skeleton className="h-9 w-full" />
                          </CommandItem>
                        )}
                        {searchEnabled && candidates.isError && (
                          <CommandItem value="__error__" disabled>
                            {tse('search.error')}
                          </CommandItem>
                        )}
                        {searchEnabled && candidates.isSuccess && candidates.data.length === 0 && (
                          <CommandItem value="__empty__" disabled>
                            {tse('search.empty')}
                          </CommandItem>
                        )}
                        {searchEnabled &&
                          candidates.isSuccess &&
                          candidates.data.map((registration) => (
                            <CommandItem
                              key={registration.tse_id}
                              value={registration.tse_id}
                              onSelect={() => selectRegistration(registration)}
                            >
                              <span className="font-medium">{registration.ballot_number}</span>
                              <span>{registration.ballot_name}</span>
                              <span className="text-muted-foreground">{registration.office}</span>
                              <Badge variant="secondary" className="ml-auto rounded-full">
                                {sentenceCase(registration.registration_status)}
                              </Badge>
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </Field>
          </div>

          {selected !== null && election !== null && (
            <div className="flex items-center gap-3 rounded-2xl bg-primary-quiet p-3">
              <Avatar size="lg">
                <AvatarFallback>{initialsOf(selected.full_name)}</AvatarFallback>
              </Avatar>
              <div className="flex flex-1 flex-col gap-1.5">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-title-sm font-semibold">{selected.ballot_name}</span>
                  {/* eslint-disable-next-line no-restricted-syntax -- decorative separator, not a word: the same glyph in every locale */}
                  <span className="text-muted-foreground">·</span>
                  <span>{selected.full_name}</span>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {(selected.coalition ?? selected.party) !== null && (
                    <Badge variant="outline" className="w-fit rounded-full bg-card uppercase">
                      {selected.coalition ?? selected.party}
                    </Badge>
                  )}
                  <Badge variant="secondary" className="w-fit rounded-full">
                    {sentenceCase(selected.registration_status)}
                  </Badge>
                </div>
              </div>
              <IconHint label={tse('link.label', { name: selected.ballot_name })}>
                <a
                  href={tseRegistrationUrl(election, selected)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 rounded-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <LinkIcon aria-hidden className="size-5" />
                </a>
              </IconHint>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
